import { err, ok, Result } from 'neverthrow';
import { EraQL, And, Relation, Expression } from '@cotera/era';
import { GraphError, InterNodeLocation } from './types';
import { Assert } from '@cotera/utilities';
import { mkEraqlExprChecker, ParseCtxFor } from './eraql-expr-checker';

export const resolveRelSource = (params: {
  source: string;
  pk: string | null;
  where: string | null;
  ctxFor: ParseCtxFor;
}): Result<
  {
    filteredRel: Relation;
    pkRes: Expression | null;
    whereRes: Expression | null;
  },
  GraphError
> => {
  const { source, pk, where, ctxFor } = params;
  const relRes = EraQL.parseRel(
    source,
    ctxFor('source', {
      bindings: {},
      attributes: {},
    })
  );

  if (relRes.isErr()) {
    const e: GraphError = {
      t: 'eraql-errors',
      errs: relRes.error,
      source,
    };

    return err(e);
  }
  const rel = relRes.value;
  const checkExpr = mkEraqlExprChecker(ctxFor);

  const pkRes = pk
    ? checkExpr('pk', pk, {
        attributes: rel,
        disallowAggregate: true,
        disallowWindow: true,
        reqTy: ['int', 'string'],
      })
    : null;

  const whereRes = where
    ? checkExpr('where', where, {
        attributes: rel,
        disallowAggregate: true,
        disallowWindow: true,
        reqTy: ['boolean'],
      })
    : null;

  const baseErrs: [loc: InterNodeLocation, err: GraphError][] = [];

  for (const res of [pkRes, whereRes]) {
    if (res?.isErr()) {
      baseErrs.push(...res.error);
    }
  }

  if (baseErrs.length > 0) {
    const e: GraphError = {
      t: 'multi-error',
      errs: baseErrs,
    };

    return err(e);
  }

  const filteredRel = rel.andThen((x) => {
    const where = whereRes ? Assert.assertOk(whereRes) : null;
    const pk = pkRes ? Assert.assertOk(pkRes).isNotNull() : null;

    if (where === null && pk === null) {
      return x;
    }

    return x.where(() => And(...[where, pk].filter((x) => x !== null)));
  });

  return ok({
    filteredRel,
    pkRes: pkRes ? Assert.assertOk(pkRes) : null,
    whereRes: whereRes ? Assert.assertOk(whereRes) : null,
  });
};
